import { useMemo, useState, type ReactNode } from 'react';
import MuiCard, { type CardProps as MuiCardProps } from '@mui/material/Card';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import Typography from '@mui/material/Typography';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useTheme } from '@mui/material/styles';
import { SurfaceContext, useSurface } from '@/atoms/Surface/surfaceContext';
import { backgroundColors, MAX_SURFACE_DEPTH } from '@/tokens/colors';
import { dsVar, nextRung, scopeToCssVars, surfaceScopes } from '@/tokens/surfaceScopes';
import { fontFamily } from '@/tokens/typography';
import { radius } from '@/tokens/radius';

/**
 * Card
 * Classification: mui-overrides (bordered, flat surface via theme)
 * MUI base: https://mui.com/material-ui/react-card/
 *
 * The default `Card` is the themed MUI Card. Two custom layout variants are
 * provided via the `variant` prop:
 * - `variant="info"` - static summary card (32px vertical / 24px horizontal padding).
 * - `variant="data"` - collapsible card for structured data (text, tables, lists).
 *
 * A Card opens a surface scope one rung above its host so nested tables pick up
 * that rung's header fill and row separators (Table Separators spec). Tables
 * inside a data card should use `bordered={false} surface="transparent"`; the
 * card lets them span its full width so the header fill is not inset like a chip.
 *
 * NOTE ON SPACING: MUI's `sx` spacing shorthands (`p`, `py`, `padding`, ...)
 * multiply numeric values by the 8px spacing unit, so paddings here are passed
 * as explicit px strings to render the exact Figma values.
 */

export { default as CardHeader } from '@mui/material/CardHeader';
export { default as CardContent } from '@mui/material/CardContent';
export { default as CardActions } from '@mui/material/CardActions';
export { default as CardMedia } from '@mui/material/CardMedia';

/** Info card: 32px top/bottom, 24px left/right. */
const INFO_PADDING = { pt: '32px', pb: '32px', px: '24px' } as const;
/** Data card vertical padding. Horizontal padding is applied to the title and
 *  to non-table body content; nested tables span the full card width so the
 *  header fill isn't inset (cell padding carries the 24px alignment). */
const DATA_PADDING_Y = { pt: '16px', pb: '16px' } as const;
const DATA_PADDING_X = '24px';
/** Gap between a card title and its body content. */
const TITLE_GAP = '16px';

export interface InfoCardProps {
  variant: 'info';
  title: string;
  children: ReactNode;
}

export interface DataCardProps {
  variant: 'data';
  title: string;
  children?: ReactNode;
  /** Shown when expanded and `children` is empty. */
  emptyText?: string;
  defaultExpanded?: boolean;
  expanded?: boolean;
  onChange?: (expanded: boolean) => void;
}

/** Props for the default (themed MUI) Card. */
export type PlainCardProps = MuiCardProps;

export type CardProps = PlainCardProps | InfoCardProps | DataCardProps;

/** Inline monospace pill for entities (users, hosts, IPs) inside card body copy. */
export function CardDataTag({ children }: { children: ReactNode }) {
  return (
    <Typography
      component="span"
      variant="caption"
      sx={{
        fontFamily: fontFamily.mono,
        color: 'text.secondary',
        bgcolor: dsVar('child', backgroundColors.surface),
        border: `1px solid ${dsVar('border', backgroundColors.border)}`,
        borderRadius: `${radius.sm}px`,
        px: '6px',
        py: '2px',
        mx: '2px',
        display: 'inline',
        verticalAlign: 'baseline',
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </Typography>
  );
}

function isEmptyChildren(children: ReactNode): boolean {
  if (children == null || children === false) return true;
  if (typeof children === 'string') return children.trim().length === 0;
  return false;
}

/**
 * Opens a host+1 surface scope on the card paper so nested tables/inputs read
 * the card's rung, not the page's. Paper elevation is forced to 0: the scope
 * already stepped, and elevation 1 would paint `--ds-child` on top of that.
 */
function CardSurface({ children, sx, style, ...cardProps }: MuiCardProps) {
  const host = useSurface();
  const mode = useTheme().palette.mode;
  const depth = host.depth + 1;
  const rung = depth > MAX_SURFACE_DEPTH ? host.rung : nextRung(host.rung);
  const scope = surfaceScopes[mode][rung];
  const value = useMemo(() => ({ depth, rung }), [depth, rung]);

  return (
    <SurfaceContext.Provider value={value}>
      <MuiCard
        {...cardProps}
        elevation={0}
        data-surface={rung}
        data-surface-depth={depth}
        style={{ ...scopeToCssVars(scope), ...style }}
        sx={[
          ...(Array.isArray(sx) ? sx : [sx]),
          {
            backgroundColor: 'var(--ds-bg)',
            color: 'var(--ds-text)',
            border: 'none',
          },
        ]}
      >
        {children}
      </MuiCard>
    </SurfaceContext.Provider>
  );
}

function InfoVariant({ title, children }: Omit<InfoCardProps, 'variant'>) {
  return (
    <CardSurface>
      <Box sx={INFO_PADDING}>
        <Typography variant="subtitle1" component="h3" sx={{ color: 'text.primary', mb: TITLE_GAP }}>
          {title}
        </Typography>
        <Box sx={{ color: 'var(--ds-text)', typography: 'body1' }}>{children}</Box>
      </Box>
    </CardSurface>
  );
}

function DataVariant({
  title,
  children,
  emptyText = 'No records available',
  defaultExpanded = true,
  expanded: expandedProp,
  onChange,
}: Omit<DataCardProps, 'variant'>) {
  const [expandedInternal, setExpandedInternal] = useState(defaultExpanded);
  const isControlled = expandedProp !== undefined;
  const expanded = expandedProp ?? expandedInternal;

  const handleToggle = () => {
    const next = !expanded;
    if (!isControlled) setExpandedInternal(next);
    onChange?.(next);
  };

  const showEmpty = expanded && isEmptyChildren(children);

  return (
    <CardSurface sx={{ overflow: 'hidden' }}>
      <Box sx={DATA_PADDING_Y}>
        <Box
          component="button"
          type="button"
          onClick={handleToggle}
          aria-expanded={expanded}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            border: 0,
            background: 'none',
            p: 0,
            m: 0,
            mx: DATA_PADDING_X,
            cursor: 'pointer',
            color: 'text.primary',
            font: 'inherit',
            textAlign: 'left',
          }}
        >
          <Typography component="span" variant="subtitle1">
            {title}
          </Typography>
          <KeyboardArrowDownIcon
            aria-hidden
            sx={{
              fontSize: 20,
              // Fixed box so rotating the chevron doesn't nudge the title.
              width: 20,
              height: 20,
              flexShrink: 0,
              transition: 'transform 0.2s ease',
              transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
            }}
          />
        </Box>
        <Collapse in={expanded} unmountOnExit={false}>
          <Box
            sx={{
              mt: TITLE_GAP,
              px: DATA_PADDING_X,
              color: 'var(--ds-text)',
              typography: 'body1',
              // Tables span the card edge so the header fill isn't inset like a
              // chip. Cell padding (24px when borderless) keeps columns aligned
              // with the title — no negative-margin bleed that shifts during
              // Collapse's overflow clipping.
              '&:has(table)': { px: 0 },
            }}
          >
            {showEmpty ? (
              <Typography variant="body1" color="text.secondary">
                {emptyText}
              </Typography>
            ) : (
              children
            )}
          </Box>
        </Collapse>
      </Box>
    </CardSurface>
  );
}

export function Card(props: CardProps) {
  if (props.variant === 'info') {
    const { title, children } = props;
    return <InfoVariant title={title} children={children} />;
  }
  if (props.variant === 'data') {
    // Disable eslint as this is the cleanest way of dropping a property without modifying the DataVariant constructor.
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { variant: _variant, ...rest } = props;
    return <DataVariant {...rest} />;
  }
  return <CardSurface {...props} />;
}
